import { Turnstile } from './Turnstile';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, type PublicTenant, type Trader } from '@ck/sdk';
import { Logo, PasswordInput } from '@ck/ui';
import { COUNTRIES } from '@ck/core';
import { api, remember, session } from './api';
import { Note } from './ui';
import { usePrefs , LANGS, type Lang } from './prefs';

type Mode = 'login' | 'signup';
interface Providers { google: { clientId: string } | null; apple: { clientId: string; redirectUri: string | null } | null }
type SocialPending = { provider: 'google' | 'apple'; idToken: string; email: string; firstName: string; lastName: string };

declare global {
  interface Window {
    google?: { accounts: { id: { initialize: (o: Record<string, unknown>) => void; renderButton: (el: HTMLElement, o: Record<string, unknown>) => void } } };
    AppleID?: { auth: { init: (o: Record<string, unknown>) => void; signIn: () => Promise<{ authorization: { id_token: string }; user?: { name?: { firstName?: string; lastName?: string } } }> } };
  }
}

const loadScript = (src: string) => new Promise<void>((ok, bad) => {
  if (document.querySelector(`script[src="${src}"]`)) { ok(); return; }
  const s = document.createElement('script'); s.src = src; s.async = true; s.onload = () => ok(); s.onerror = () => bad(new Error('load')); document.head.appendChild(s);
});
const reduced = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A desk lamp drawn for CK. The light switches on when the page opens and flickers when the form changes. */
function Lamp({ on, pulling, onPull }: { on: boolean; pulling: boolean; onPull: () => void }) {
  return (
    <div className={`lamp${on ? ' on' : ''}${pulling ? ' pull' : ''}`}>
      <div className="lamp-beam" aria-hidden="true" />
      <svg viewBox="0 0 300 420" className="lamp-svg" aria-hidden="true">
        <defs>
          <linearGradient id="lmMetal" x1="0" x2="1"><stop offset="0" stopColor="#2A2A2E" /><stop offset=".55" stopColor="#4A4A50" /><stop offset="1" stopColor="#1C1C20" /></linearGradient>
          <linearGradient id="lmBrass" x1="0" x2="1"><stop offset="0" stopColor="#8A5B16" /><stop offset=".5" stopColor="#E4A940" /><stop offset="1" stopColor="#7A4E10" /></linearGradient>
          <radialGradient id="lmInside" cx=".5" cy=".2" r=".9"><stop offset="0" stopColor="#FFE7B0" /><stop offset=".5" stopColor="#F5A93B" /><stop offset="1" stopColor="#7A4508" /></radialGradient>
          <radialGradient id="lmBulb" cx=".5" cy=".5" r=".5"><stop offset="0" stopColor="#FFFDF2" /><stop offset=".6" stopColor="#FFD780" /><stop offset="1" stopColor="#FFB547" stopOpacity="0" /></radialGradient>
        </defs>
        {/* base */}
        <ellipse cx="120" cy="396" rx="78" ry="14" fill="#0A0A0C" />
        <path d="M44 392 Q120 366 196 392 L196 398 Q120 410 44 398 Z" fill="url(#lmMetal)" />
        <ellipse cx="120" cy="388" rx="20" ry="6" fill="url(#lmBrass)" />
        {/* lower arm */}
        <rect x="114" y="236" width="10" height="152" rx="5" transform="rotate(-14 119 388)" fill="url(#lmMetal)" />
        <circle cx="82" cy="244" r="12" fill="url(#lmBrass)" /><circle cx="82" cy="244" r="5" fill="#2A1B05" />
        {/* upper arm */}
        <rect x="78" y="118" width="9" height="130" rx="4.5" transform="rotate(38 82 244)" fill="url(#lmMetal)" />
        <circle cx="162" cy="140" r="10" fill="url(#lmBrass)" /><circle cx="162" cy="140" r="4" fill="#2A1B05" />
        {/* neck and shade: a cone tilted towards the form */}
        <line x1="162" y1="140" x2="200" y2="150" stroke="url(#lmMetal)" strokeWidth="9" strokeLinecap="round" />
        <g className="lamp-head">
          <g transform="translate(200 150) rotate(-35)">
            <path d="M-16 -4 Q0 -14 16 -4 L54 66 Q0 90 -54 66 Z" fill="url(#lmMetal)" />
            <path d="M-12 -2 Q0 -9 12 -2 L20 12 Q0 4 -20 12 Z" fill="#55555C" opacity=".6" />
            <ellipse cx="0" cy="70" rx="54" ry="13" fill="#141416" />
            <ellipse className="lamp-inside" cx="0" cy="71" rx="48" ry="10" fill="url(#lmInside)" />
            <circle className="lamp-bulb" cx="0" cy="74" r="24" fill="url(#lmBulb)" />
          </g>
        </g>
        {/* pull chain */}
        <g className="lamp-chain">
          <line x1="262" y1="204" x2="262" y2="280" stroke="#C9A050" strokeWidth="1.5" strokeDasharray="2 3" />
          <circle cx="262" cy="286" r="6" fill="url(#lmBrass)" />
        </g>
      </svg>
      <button type="button" className="lamp-pull" onClick={onPull} aria-pressed={on} aria-label={on ? 'Switch the lamp off' : 'Switch the lamp on'} />
    </div>
  );
}

function Social({ providers, mode, onToken }: { providers: Providers; mode: Mode; onToken: (p: 'google' | 'apple', idToken: string, first?: string, last?: string) => void }) {
  const g = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!providers.google || !g.current) return;
    let live = true;
    loadScript('https://accounts.google.com/gsi/client').then(() => {
      if (!live || !window.google || !g.current) return;
      window.google.accounts.id.initialize({ client_id: providers.google!.clientId, callback: (r: { credential: string }) => onToken('google', r.credential), ux_mode: 'popup' });
      window.google.accounts.id.renderButton(g.current, { theme: 'filled_black', size: 'large', shape: 'pill', text: mode === 'signup' ? 'signup_with' : 'signin_with', width: Math.min(360, g.current.clientWidth || 320) });
    }).catch(() => setErr('Google sign-in could not load. Check your connection.'));
    return () => { live = false; };
  }, [providers.google?.clientId, mode]);
  async function apple() {
    setErr(null);
    try {
      await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
      window.AppleID!.auth.init({ clientId: providers.apple!.clientId, scope: 'name email', redirectURI: providers.apple!.redirectUri || window.location.origin, usePopup: true });
      const r = await window.AppleID!.auth.signIn();
      onToken('apple', r.authorization.id_token, r.user?.name?.firstName, r.user?.name?.lastName);
    } catch (e) { if ((e as { error?: string }).error !== 'popup_closed_by_user') setErr('Apple sign-in did not finish. Try again.'); }
  }
  const off = (who: string) => setErr(`${who} ${mode === 'signup' ? 'sign-up' : 'sign-in'} is being switched on. Use your email for now — you can link ${who} later.`);
  const G = <svg viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.7H9v3.3h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.5z"/><path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8z"/><path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.5 1.4l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z"/></svg>;
  return (
    <div className="social">
      <div className="or"><span>or continue with</span></div>
      <div className="social-b">
        {providers.google ? <div ref={g} className="g-btn" /> : <button type="button" className="apple-btn google" onClick={() => off('Google')}>{G}{mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google'}</button>}
        {providers.apple && <button type="button" className="apple-btn" onClick={apple}><svg viewBox="0 0 17 20" aria-hidden="true"><path fill="currentColor" d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.8 1.2 1.8 2.6 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.5.9-1.4 1.3-2.7 1.3-2.8 0 0-2.6-1-2.6-4.1zM11.6 3c.7-.8 1.1-1.9 1-3-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.8-1 2.9 1.1.1 2.2-.6 2.9-1.4z" /></svg>{mode === 'signup' ? 'Sign up with Apple' : 'Sign in with Apple'}</button>}
      </div>
      {err && <Note kind="err">{err}</Note>}
    </div>
  );
}

export function Auth({ tenant, onSignedIn }: { tenant: PublicTenant; onSignedIn: (t: Trader) => void }) {
  const savedRef = remember('ck.ref');
  const [refInput, setRefInput] = useState(savedRef ?? '');
  const [refCheck, setRefCheck] = useState<{ valid: boolean; partner: string | null } | null>(null);
  // Accepts a bare code or a whole link with ?ref=CODE.
  const ref = (() => { const v = refInput.trim(); if (!v) return null; const m = v.match(/[?&]ref=([A-Za-z0-9-]+)/); return (m ? m[1]! : v).toUpperCase().replace(/[^A-Z0-9-]/g, '') || null; })();
  useEffect(() => {
    if (!ref) { setRefCheck(null); return; }
    const t = setTimeout(() => { api.get<{ valid: boolean; partner: string | null }>(`/v1/ref/${encodeURIComponent(ref)}/check`).then(setRefCheck).catch(() => setRefCheck(null)); }, 350);
    return () => clearTimeout(t);
  }, [ref]);
  // The website's "Get started" links open the portal at #signup.
  const [mode, setMode] = useState<Mode>(remember('ck.ref') || /^#\/?signup\b/.test(location.hash) ? 'signup' : 'login');
  const [f, setF] = useState({ email: '', password: '', code: '', firstName: '', lastName: '', country: 'Tanzania', phone: '', accept: false });
  const [needsCode, setNeedsCode] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [twoStep, setTwoStep] = useState(false);
  const [via, setVia] = useState<'app' | 'email'>('app');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [light, setLight] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [providers, setProviders] = useState<Providers>({ google: null, apple: null });
  const [pending, setPending] = useState<SocialPending | null>(null);
  const { t, lang, setLang } = usePrefs();
  const b = tenant.branding;
  // Forgotten password: ask for a link, then the emailed link opens the portal at #reset=TOKEN.
  const [recover, setRecover] = useState<null | { step: 'ask' | 'sent' | 'reset' | 'done' | 'bad'; token?: string; who?: string; msg?: string }>(() => {
    const m = /[#&]reset=([A-Za-z0-9_-]{20,})/.exec(location.hash);
    if (!m) return null;
    history.replaceState(null, '', location.pathname + location.search);
    return { step: 'reset', token: m[1]! };
  });
  const [newPw, setNewPw] = useState({ a: '', b: '' });
  useEffect(() => {
    if (recover?.step !== 'reset' || !recover.token || recover.who) return;
    api.post<{ email: string }>('/v1/auth/reset/check', { token: recover.token }).then((r) => setRecover((c) => (c ? { ...c, who: r.email } : c)))
      .catch((x) => setRecover({ step: 'bad', msg: x instanceof Error ? x.message : 'This link no longer works.' }));
  }, [recover?.step, recover?.token]);
  async function askLink(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try { const r = await api.post<{ message: string }>('/v1/auth/forgot', { email: f.email }); setRecover({ step: 'sent', msg: r.message, who: f.email }); }
    catch (x) { setError(x instanceof Error ? x.message : 'Something went wrong.'); } finally { setBusy(false); }
  }
  async function saveNew(e: FormEvent) {
    e.preventDefault();
    if (newPw.a !== newPw.b) { setError('The two passwords do not match.'); return; }
    setBusy(true); setError(null);
    try { await api.post('/v1/auth/reset', { token: recover?.token, password: newPw.a }); setRecover({ step: 'done' }); setF((c) => ({ ...c, password: '' })); }
    catch (x) { setError(x instanceof Error ? x.message : 'Something went wrong.'); } finally { setBusy(false); }
  }
  const leaveRecover = () => { setRecover(null); setError(null); setNewPw({ a: '', b: '' }); setMode('login'); };
  const open = tenant.markets.filter((m) => m.status === 'open');

  // Pull the chain and switch the light on shortly after the page opens.
  useEffect(() => {
    if (reduced()) { setLight(true); return; }
    const a = setTimeout(() => setPulling(true), 450);
    const c = setTimeout(() => { setLight(true); setPulling(false); }, 800);
    return () => { clearTimeout(a); clearTimeout(c); };
  }, []);
  useEffect(() => { api.get<Providers>('/v1/auth/providers').then(setProviders).catch(() => undefined); }, []);

  const pull = (after?: () => void) => {
    if (reduced()) { after?.(); return; }
    setPulling(true);
    setTimeout(() => { after?.(); setPulling(false); }, 260);
  };
  const switchMode = (m: Mode) => {
    if (m === mode) return;
    setError(null); setNeedsCode(false); setPending(null);
    // A quick off-and-on as the form changes.
    pull(() => { setLight(false); setMode(m); setTimeout(() => setLight(true), 170); });
  };

  const [human, setHuman] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  async function finish(res: { token: string; trader: Trader }) { session.set(res.token); onSignedIn(res.trader); }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'signup' && ref && refCheck && !refCheck.valid) { setError('That affiliate code is not valid. Fix it or leave it empty.'); return; }
    setBusy(true); setError(null);
    try {
      if (pending) {
        await finish(await api.post('/v1/auth/social', { provider: pending.provider, idToken: pending.idToken, firstName: f.firstName, lastName: f.lastName, country: f.country, acceptTerms: f.accept, ref }));
        return;
      }
      const res = mode === 'login'
        ? await api.post<{ token: string; trader: Trader }>('/v1/auth/login', { email: f.email, password: f.password, code: needsCode ? f.code : undefined, via: twoStep ? via : undefined, turnstileToken: human })
        : await api.post<{ token: string; trader: Trader }>('/v1/auth/signup', { turnstileToken: human, email: f.email, password: f.password, firstName: f.firstName, lastName: f.lastName, country: f.country, phone: f.phone || null, ref, acceptTerms: f.accept });
      await finish(res);
    } catch (x) {
      if (x instanceof ApiError && x.body.needsCode) { if (x.body.twoStep || !x.body.emailCode) setTwoStep(true); if (x.body.emailCode) setEmailCode(x.message); if (needsCode || x.body.expired) setError(x.message); if (x.body.expired) { setNeedsCode(false); setF({ ...f, code: '' }); } else setNeedsCode(true); }
      else setError(x instanceof Error ? x.message : 'Something went wrong.');
      setTries((n) => n + 1);
    } finally { setBusy(false); }
  }

  async function social(provider: 'google' | 'apple', idToken: string, first?: string, last?: string) {
    setBusy(true); setError(null);
    try {
      await finish(await api.post('/v1/auth/social', { provider, idToken, firstName: first, lastName: last, ref }));
    } catch (x) {
      if (x instanceof ApiError && x.body.needsProfile) {
        const p = x.body as unknown as { email: string; firstName: string; lastName: string };
        setPending({ provider, idToken, email: p.email, firstName: p.firstName, lastName: p.lastName });
        setF((cur) => ({ ...cur, firstName: p.firstName || first || '', lastName: p.lastName || last || '' }));
        pull(() => { setLight(false); setMode('signup'); setTimeout(() => setLight(true), 170); });
      } else setError(x instanceof Error ? x.message : 'Something went wrong.');
    } finally { setBusy(false); }
  }

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className={`lamp-stage${light ? ' lit' : ''}`}>
      <div className="dust" aria-hidden="true">{Array.from({ length: 28 }, (_, i) => <i key={i} style={{ ['--x' as string]: `${(i * 37) % 100}%`, ['--d' as string]: `${8 + (i % 7) * 2}s`, ['--delay' as string]: `${-(i * 1.3) % 12}s`, ['--s' as string]: `${1 + (i % 3)}px` }} />)}</div>
      <header className="lamp-top"><a href="https://ckfundedpro.com" aria-label="CK Funded Pro website"><Logo branding={b} on="dark" height={32} /></a><span>{t('Client portal')}</span></header>
      <div className="lamp-scene"><Lamp on={light} pulling={pulling} onPull={() => pull(() => setLight((v) => !v))} /></div>
      <main className="lamp-card">
        {recover ? (
          <div className="lamp-card-in" key={`r-${recover.step}`}>
            {recover.step === 'ask' && <>
              <h1>Reset your password</h1>
              <p className="lamp-sub">Enter the email you signed up with. We’ll send a link to choose a new password.</p>
              <form className="form" onSubmit={askLink}>
                <label>{t('Email')}<input type="email" required autoFocus autoComplete="email" placeholder="you@example.com" value={f.email} onChange={set('email')} /></label>
                {error && <Note kind="err">{error}</Note>}
                <button className="lamp-go" disabled={busy}>{busy ? t('Please wait…') : 'Send reset link'}<span aria-hidden="true">→</span></button>
                <button type="button" className="link" onClick={leaveRecover}>Back to sign in</button>
              </form>
            </>}
            {recover.step === 'sent' && <>
              <h1>Check your email</h1>
              <p className="lamp-sub">{recover.msg}</p>
              <p className="fine">Sent to <b>{recover.who}</b>. Open the email and tap <b>Choose a new password</b>. Not there after a few minutes? Check spam, or send it again.</p>
              <div className="form">
                <button type="button" className="lamp-go" onClick={() => setRecover({ step: 'ask' })}>Send it again<span aria-hidden="true">↻</span></button>
                <button type="button" className="link" onClick={leaveRecover}>Back to sign in</button>
              </div>
            </>}
            {recover.step === 'reset' && <>
              <h1>Choose a new password</h1>
              <p className="lamp-sub">{recover.who ? `For ${recover.who}.` : 'Checking your link…'} You’ll be signed out on other devices.</p>
              <form className="form" onSubmit={saveNew}>
                <label>New password<PasswordInput required minLength={10} autoFocus autoComplete="new-password" value={newPw.a} onChange={(e) => setNewPw({ ...newPw, a: e.target.value })} /><small>At least 10 characters, with letters and a number.</small></label>
                <label>Type it again<PasswordInput required minLength={10} autoComplete="new-password" value={newPw.b} onChange={(e) => setNewPw({ ...newPw, b: e.target.value })} /></label>
                {error && <Note kind="err">{error}</Note>}
                <button className="lamp-go" disabled={busy || !recover.who}>{busy ? t('Please wait…') : 'Save new password'}<span aria-hidden="true">→</span></button>
              </form>
            </>}
            {recover.step === 'done' && <>
              <h1>Password changed</h1>
              <p className="lamp-sub">Sign in with your new password.</p>
              <div className="form"><button type="button" className="lamp-go" onClick={leaveRecover}>{t('Sign in')}<span aria-hidden="true">→</span></button></div>
            </>}
            {recover.step === 'bad' && <>
              <h1>Link expired</h1>
              <p className="lamp-sub">{recover.msg}</p>
              <div className="form">
                <button type="button" className="lamp-go" onClick={() => { setError(null); setRecover({ step: 'ask' }); }}>Send a new link<span aria-hidden="true">→</span></button>
                <button type="button" className="link" onClick={leaveRecover}>Back to sign in</button>
              </div>
            </>}
          </div>
        ) : (
        <div className="lamp-card-in" key={pending ? 'profile' : mode}>
          <h1>{pending ? 'Almost there' : mode === 'signup' ? 'Create your account' : 'Welcome back'}</h1>
          <p className="lamp-sub">{pending ? `Signed in as ${pending.email}. Tell us where you trade from.` : mode === 'signup' ? `One account for ${open.map((m) => m.name).join(', ')}.` : 'Sign in to continue to your accounts.'}</p>
          {!pending && <div className="lamp-tabs" role="tablist" data-on={mode}>
            <span className="lamp-tab-thumb" aria-hidden="true" />
            <button role="tab" aria-selected={mode === 'login'} onClick={() => switchMode('login')}>{t('Sign in')}</button>
            <button role="tab" aria-selected={mode === 'signup'} onClick={() => switchMode('signup')}>{t('Sign up')}</button>
          </div>}
          <form onSubmit={submit} className="form">
            {needsCode ? (
              <label>{emailCode ? 'Code from your email' : 'Code from your authenticator app'}
                <input className="code-in" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required autoFocus value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
                <button type="button" className="otp-paste" onClick={async () => { try { const t = await navigator.clipboard.readText(); const m = /\d{6}/.exec(t.replace(/\s/g, '')); if (m) setF({ ...f, code: m[0] }); else setError('No 6-digit code found. Copy the code from the email, then tap Paste code.'); } catch { setError('Your browser blocked pasting. Long-press the box and choose Paste.'); } }}>📋 Paste code</button>
                <small>{emailCode || `Two-step sign-in is on for ${f.email}.`}</small>
                {twoStep && <span className="otp-via" role="group" aria-label="Where to get your code">
                  <button type="button" className={via === 'app' ? 'on' : ''} onClick={() => { setVia('app'); setEmailCode(''); setError(''); setF({ ...f, code: '' }); }}>Authenticator app</button>
                  <button type="button" className={via === 'email' ? 'on' : ''} disabled={busy} onClick={async () => {
                    setVia('email'); setError(''); setF({ ...f, code: '' }); setBusy(true);
                    try { await api.post('/v1/auth/login', { email: f.email, password: f.password, via: 'email' }); } catch (x) { if (x instanceof ApiError && x.body.emailCode) setEmailCode(x.message); else setError(x instanceof Error ? x.message : 'Could not send the code.'); } finally { setBusy(false); }
                  }}>{via === 'email' ? 'Resend email code' : 'Email me a code'}</button>
                </span>}
              </label>
            ) : (
              <>
                {(mode === 'signup' || pending) && (
                  <div className="row2">
                    <label>{t('First name')}<input required autoComplete="given-name" value={f.firstName} onChange={set('firstName')} /></label>
                    <label>{t('Last name')}<input required autoComplete="family-name" value={f.lastName} onChange={set('lastName')} /></label>
                  </div>
                )}
                {!pending && <label>{t('Email')}<input type="email" required autoComplete="email" placeholder="you@example.com" value={f.email} onChange={set('email')} /></label>}
                {!pending && <label><span className="lbl-row">{t('Password')}{mode === 'login' && <button type="button" className="link" onClick={() => { setError(null); setRecover({ step: 'ask' }); }}>Forgot?</button>}</span>
                  <PasswordInput required minLength={mode === 'signup' ? 10 : 1} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={f.password} onChange={set('password')} />
                  {mode === 'signup' && <small>At least 10 characters, with letters and a number.</small>}
                </label>}
                {(mode === 'signup' || pending) && (
                  <>
                    <div className="row2">
                      <label>{t('Country')}<select value={f.country} onChange={set('country')}>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                      {!pending && <label>{t('Phone (optional)')}<input type="tel" autoComplete="tel" placeholder="+255" value={f.phone} onChange={set('phone')} /></label>}
                    </div>
                    <label>Affiliate code <small>Optional. A code or link from the partner who invited you.</small>
                      <input autoComplete="off" placeholder="e.g. AMANI-FX" value={refInput} onChange={(e) => setRefInput(e.target.value)} />
                      {ref && refCheck && (refCheck.valid ? <small className="ref-ok">Code accepted{refCheck.partner ? `: invited by ${refCheck.partner}` : ''}.</small> : <small className="ref-bad">We don’t recognise this code. Check it, or leave it empty.</small>)}
                    </label>
                    <label className="check"><input type="checkbox" checked={f.accept} onChange={(e) => setF({ ...f, accept: e.target.checked })} required /> I am 18 or older and accept the terms, privacy policy and risk disclosure.</label>
                  </>
                )}
              </>
            )}
            {error && <Note kind="err">{error}</Note>}
            {!pending && <Turnstile onToken={setHuman} resetKey={tries} />}
            <button className="lamp-go" disabled={busy}>{busy ? t('Please wait…') : pending ? 'Create my account' : needsCode ? 'Verify and sign in' : mode === 'login' ? t('Sign in') : t('Create my account')}<span aria-hidden="true">→</span></button>
            {pending && <button type="button" className="link" onClick={() => setPending(null)}>Use email instead</button>}
          </form>
          {!pending && !needsCode && <Social providers={providers} mode={mode} onToken={social} />}
          <p className="lamp-help">Need help? <a href={`mailto:${b.supportEmail}`}>{b.supportEmail}</a> · <select className="lamp-lang" value={lang} aria-label="Language" onChange={(e) => setLang(e.target.value as Lang)}>{LANGS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></p>
        </div>
        )}
      </main>
      <footer className="lamp-foot">© {new Date().getFullYear()} {b.name}</footer>
    </div>
  );
}
